import { useState } from "react";
import { userService } from "../../services/userService";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import {
  parseApiError,
  formatValidationErrors,
} from "../../utils/errorHandler";
import { useAuth } from "../../contexts/AuthContext";
import { PermissionsSelector } from "../users/PermissionsSelector";
import { Permission } from "../../types";

interface AddAdminDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddAdminDialog({
  isOpen,
  onClose,
  onSuccess,
}: AddAdminDialogProps) {
  const { isSuperAdmin: currentUserIsSuperAdmin } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [newIsSuperAdmin, setNewIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const { showSuccess, showError } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Only super admins can create admins with permissions
      if (!currentUserIsSuperAdmin) {
        throw new Error("Only super admins can create new admins.");
      }

      const userData: any = {
        ...formData,
        role: "admin",
        skipEmailVerification: true,
      };

      // Include permissions
      userData.permissions = newIsSuperAdmin ? [] : selectedPermissions;
      userData.isSuperAdmin = newIsSuperAdmin;

      await userService.createUser(userData);
      showSuccess("Admin created successfully", "Success");
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        username: "",
        email: "",
        password: "",
        firstName: "",
        lastName: "",
      });
      setSelectedPermissions([]);
      setNewIsSuperAdmin(false);
    } catch (err: any) {
      console.error("Create admin error:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(apiError.message, "Create Failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Admin">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            <p className="font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-xs">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="text-sm font-medium text-gray-700"
            >
              First Name
            </label>
            <Input
              id="firstName"
              placeholder="Admin's first name"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="text-sm font-medium text-gray-700"
            >
              Last Name
            </label>
            <Input
              id="lastName"
              placeholder="Admin's last name"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="username"
            className="text-sm font-medium text-gray-700"
          >
            Username
          </label>
          <Input
            id="username"
            placeholder="Choose a username"
            value={formData.username}
            onChange={handleChange}
            required
            autoComplete="username"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address
          </label>
          <Input
            id="email"
            type="email"
            placeholder="admin@example.com"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="text-sm font-medium text-gray-700"
          >
            Password
          </label>
          <Input
            id="password"
            type="password"
            placeholder="Strong password"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
          />
        </div>

        {/* Admin-specific fields - Permissions */}
        <div className="space-y-2">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="isSuperAdmin"
              checked={newIsSuperAdmin}
              onChange={(e) => {
                setNewIsSuperAdmin(e.target.checked);
                if (e.target.checked) {
                  setSelectedPermissions([]);
                }
              }}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <label
                htmlFor="isSuperAdmin"
                className="text-sm font-medium text-gray-900 cursor-pointer"
              >
                Super Admin
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Super admins have all permissions and can manage other admins.
              </p>
            </div>
          </div>
        </div>

        {!newIsSuperAdmin && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Permissions
            </label>
            <div className="max-h-64 overflow-y-auto">
              <PermissionsSelector
                selectedPermissions={selectedPermissions}
                onChange={setSelectedPermissions}
                compact
              />
            </div>
          </div>
        )}


        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-32"
          >
            {isLoading ? "Creating..." : "Create Admin"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
